import React, { useEffect, useState, useRef } from "react";
import ApiService from "../../services/api-service";
import { CircularProgress, Box, Typography } from "@mui/material";
import FeedCard from "../../components/Feed/FeedCard";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import "./Feed.scss";

const VISIBILITY_THRESHOLD = 0.6;

const Feed = () => {
  const [latestMusic, setLatestMusic] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlayingIdx, setCurrentPlayingIdx] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [userPausedIdx, setUserPausedIdx] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showPlaylist, setShowPlaylist] = useState(false);
    const [userSaves, setUserSaves] = useState([]); // ← Add this line!


  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [cursor, setCursor] = useState(new Date().toISOString()); // Start from now

  const cardRefs = useRef([]);
  const visibilityMap = useRef({});
  const lastActiveIdx = useRef(null);

   // Fetch user's saved songs
  const fetchUserSaves = async () => {
    try {
      const response = await ApiService.GET_SAVES('music');
      const savedSongIds = response.data || [];
      setUserSaves(savedSongIds);
      console.log('[Feed] User saves loaded:', savedSongIds.length);
    } catch (error) {
      console.error('[Feed] Error fetching user saves:', error);
    }
  };

   // Handle save toggle
  const handleSaveToggle = (itemId, isSaved) => {
    if (isSaved) {
      setUserSaves([...userSaves, itemId]);
    } else {
      setUserSaves(userSaves.filter(id => id !== itemId));
    }
  };


  // Fetch feed data (with cursor-based pagination)
const fetchFeeds = async (append = false, cursorValue = null) => {
  try {
    if (append) setFetchingMore(true);
    else setLoading(true);

    // Use the provided cursor or the current state
    const cursorToUse = cursorValue || cursor;
    const params = {
      direction: "older",
      cursor: cursorToUse,
    };

    console.log(`[Feed] Fetching feeds. append=${append}, cursor=${cursorToUse}`);
    const musicResponse = await ApiService.FETCH_MUSIC_FEED(params);
    const { latestMusic: newMusic } = musicResponse.result || {};

    console.log(`[Feed] Received ${newMusic?.length || 0} songs from API`);
    if (newMusic && newMusic.length > 0) {
      setLatestMusic(prev => {
        // Combine previous and new music if appending, else just use new music
        const combined = append ? [...prev, ...newMusic] : newMusic;
        // Filter out duplicates by filePath or title+artist
        const seen = new Set();
        return combined.filter(song => {
          const key = song.filePath || (song.title + song.artist);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      setHasMore(true);

      // Update cursor to the releaseDate of the last song
      const last = newMusic[newMusic.length - 1];
      if (last && last.releaseDate) {
        setCursor(last.releaseDate);
      }
    } else {
      setHasMore(false);
      console.log("[Feed] No more songs to load.");
    }
  } catch (err) {
    setError("Failed to load feeds.");
    console.error("[Feed] Error fetching feeds:", err);
  } finally {
    setLoading(false);
    setFetchingMore(false);
  }
};

  // Initial fetch
  useEffect(() => {
    setCursor(new Date().toISOString());
    fetchFeeds(false, new Date().toISOString());
    fetchUserSaves(); // Fetch user saves on mount

    // eslint-disable-next-line
  }, []);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector('.feed-scroll-outer');
      if (!scrollContainer || loading || fetchingMore || !hasMore) return;
      const scrollPos = scrollContainer.scrollTop + scrollContainer.clientHeight;
      const scrollHeight = scrollContainer.scrollHeight;
      if (scrollPos >= scrollHeight - 100) {
        // Only fetch if not already fetching and there are more
        if (hasMore && !fetchingMore) {
          fetchFeeds(true, cursor);
        }
      }
    };

    const scrollContainer = document.querySelector('.feed-scroll-outer');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loading, fetchingMore, hasMore, cursor]);

  // Intersection Observer for card visibility and auto-fetch when near end
  useEffect(() => {
    if (!latestMusic.length) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        let mostVisibleIdx = null;
        let maxRatio = 0;
        const newVisibility = { ...visibilityMap.current };

        entries.forEach((entry) => {
          const idx = Number(entry.target.dataset.idx);
          const ratio = entry.intersectionRatio;
          newVisibility[idx] = ratio;
          if (ratio > maxRatio) {
            maxRatio = ratio;
            mostVisibleIdx = idx;
          }
        });

        visibilityMap.current = newVisibility;

        // Find the most visible card above threshold
        if (
          mostVisibleIdx !== null &&
          newVisibility[mostVisibleIdx] >= VISIBILITY_THRESHOLD
        ) {
          if (lastActiveIdx.current !== mostVisibleIdx) {
            setCurrentPlayingIdx(mostVisibleIdx);
            setIsPaused(false);
            setUserPausedIdx(null);
            lastActiveIdx.current = mostVisibleIdx;
            // Trigger fetch when 2nd-to-last or last card is visible
            if (
              mostVisibleIdx >= latestMusic.length - 3 &&
              hasMore &&
              !fetchingMore
            ) {
              fetchFeeds(true, cursor);
            }
          } else if (isPaused && userPausedIdx !== mostVisibleIdx) {
            setIsPaused(false);
          }
        } else if (
          lastActiveIdx.current !== null &&
          newVisibility[lastActiveIdx.current] !== undefined &&
          newVisibility[lastActiveIdx.current] < VISIBILITY_THRESHOLD
        ) {
          if (!isPaused) {
            setIsPaused(true);
          }
        }
      },
      {
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line
  }, [latestMusic, isPaused, userPausedIdx, hasMore, fetchingMore, cursor]);

  // Handle play/pause from user interaction
  const handleCardPlay = (idx) => {
    if (currentPlayingIdx === idx) {
      setIsPaused((prev) => {
        if (!prev) setUserPausedIdx(idx);
        else setUserPausedIdx(null);
        return !prev;
      });
    } else {
      setCurrentPlayingIdx(idx);
      setIsPaused(false);
      setUserPausedIdx(null);
      lastActiveIdx.current = idx;
    }
    console.log(`[Feed] handleCardPlay: idx=${idx}`);
  };

  if (loading && latestMusic.length === 0) {
    return (
      <Box className="feed-loading" sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="feed-error" sx={{ textAlign: "center", padding: "20px" }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="feed-scroll-outer"
      sx={{
        backgroundColor: "#000",
        minHeight: "100vh",
        overflowY: "auto",
        position: "relative",
      }}
    >
      <Box
        className="feed-scroll-inner"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {showPlaylist && (
          <AddMusicToPlaylist
            musicId={selectedAlbum}
            open={showPlaylist}
            handleClose={() => setShowPlaylist(false)}
          />
        )}
        {latestMusic.map((item, idx) => (
          <FeedCard
            key={item._id}
            item={item}
            index={idx}
            isPlaying={currentPlayingIdx === idx && !isPaused}
            isPaused={isPaused && currentPlayingIdx === idx}
            onPlay={() => handleCardPlay(idx)}
            cardRef={el => (cardRefs.current[idx] = el)}
            data-idx={idx}
            setSelectedAlbum={setSelectedAlbum}
            setShowPlaylist={setShowPlaylist}
            userSaves={userSaves} // Pass userSaves
            onSaveToggle={handleSaveToggle} // Pass the handler


          />
        ))}
        {fetchingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!hasMore && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
            No more songs to load.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Feed;